import React from 'react';
import { CommunityComment } from 'types/ApiServicesTypes';
import { Box, Typography, Avatar, useTheme } from '@mui/material';
import { Favorite, FavoriteBorder, HourglassEmpty } from '@mui/icons-material';
import { useLikeCommunityComment } from 'services/Community';

interface CommentProps {
  comment: CommunityComment;
}

const Comment: React.FC<CommentProps> = ({ comment }) => {
  const theme = useTheme();
  const likeCommentMutation = useLikeCommunityComment();

  // Handle like click
  const handleLikeClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    likeCommentMutation.mutate(comment.id);
  };

  return (
    <Box sx={{ 
      border: '1px solid #eee', 
      borderRadius: 2, 
      padding: 2, 
      marginBottom: 2,
      backgroundColor: theme.palette.common.white
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
        <Avatar 
          src={comment.author.avatar} 
          alt={comment.author.name}
          sx={{ width: 32, height: 32, marginRight: 1 }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {comment.author.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(comment.created_at).toLocaleString()}
          </Typography>
        </Box>
        {comment.author.badge && (
          <Typography 
            variant="caption" 
            sx={{ 
              background: '#e6f4ea', 
              color: '#219653', 
              fontWeight: 700, 
              borderRadius: 1, 
              padding: '2px 8px',
              marginRight: 1
            }}
          >
            {comment.author.badge}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" sx={{ marginTop: 1, marginBottom: 1, marginLeft: 1 }}>
        {comment.content}
      </Typography>
      
      {/* Like Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginTop: 1 }}>
        <Box
          onClick={handleLikeClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: likeCommentMutation.isLoading ? 'not-allowed' : 'pointer',
            padding: '4px 2px',
            borderRadius: theme.shape.borderRadius,
            opacity: likeCommentMutation.isLoading ? 0.6 : 1,
            color: comment.is_liked ? theme.palette.error.main : theme.palette.text.secondary,
            transition: theme.transitions.create(['background-color', 'color', 'opacity', 'transform'], {
              duration: theme.transitions.duration.short,
            }),
            '&:hover': {
              backgroundColor: likeCommentMutation.isLoading ? 'transparent' : theme.palette.action.hover,
              transform: likeCommentMutation.isLoading ? 'none' : 'scale(1.05)',
            },
          }}
        >
          {likeCommentMutation.isLoading ? (
            <HourglassEmpty sx={{ fontSize: 16 }} aria-label="Loading" />
          ) : comment.is_liked ? (
            <Favorite sx={{ fontSize: 16 }} aria-label="Liked" />
          ) : (
            <FavoriteBorder sx={{ fontSize: 16 }} aria-label="Like" />
          )}
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {comment.likes_count}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Comment; 